import {
  DELIVERABLE_FORMATS, DELIVERABLE_FORMAT_PLATFORM, deliverableBuckets, deliverableFormatLabel, deliverableLabel, deliverableStatusLabel, isDeliverableFormat,
  nextDue, overdueKind, plannedSeries, summarizeDeliverables, type DeliverableBucket, type DeliverableStatus,
} from "@talentkontor/domain";
import type { Pill } from "../../../model/invoice-view";
import { date } from "../../../lib/format";
import { nameOfTalent, pill, platformLabel } from "./model";
import type { BrandRow, CatalogEntry, DealFull, PeriodFull, TalentRow } from "./typen";
import { t, currentLang } from "../../../lib/sprache";

/**
 * Einzelbeiträge einer Kooperation (0068) in der Agentur: Anzeige, Formular („mehrere gleiche anlegen“), Übersicht mit
 * Filtern. Reine Funktionen; Fristen und Filterregeln kommen aus der Fachlogik (packages/domain/src/deliverables.ts).
 */

/** Zeile aus deal_deliverables (Team). */
export interface BeitragRow {
  id: string;
  org_id: string;
  deal_id: string;
  period_id: string | null;
  platform: string;
  format: string;
  format_label: string | null;
  title: string | null;
  draft_due: string | null;
  publish_on: string | null;
  status: DeliverableStatus;
  live_url: string | null;
  published_on: string | null;
  live_reported_by: string | null;
  live_reported_at: string | null;
  /** Entwurf vorab freigeben (0095); nein = der Creator meldet direkt live */
  approval_required?: boolean;
  label_checked: boolean;
  label_checked_by: string | null;
  label_checked_at: string | null;
  usage_rights_months: number | null;
  usage_rights_until: string | null;
  /** 0199: Art der Nutzung, einfach/ausschließlich, Gebiet */
  usage_kinds?: string[] | null;
  usage_exclusive?: boolean | null;
  usage_territory?: string | null;
  note: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/** Stände in der Reihenfolge der Arbeit (Auswahl „Stand setzen“). */
export const STAENDE: readonly DeliverableStatus[] = ["planned", "draft_submitted", "changes_requested", "approved", "live", "cancelled"];

const STAND_TON: Record<DeliverableStatus, Pill["tone"]> = {
  planned: "neutral", draft_submitted: "warning", changes_requested: "warning", approved: "info", live: "success", cancelled: "neutral",
};

/** Pille des Stands; überfällig geht vor (rot, mit Art der Frist). */
export function beitragPill(x: Pick<BeitragRow, "status" | "draft_due" | "publish_on">, today: string): Pill {
  const k = overdueKind(x, today);
  if (k) return pill(k === "publish" ? t("Veröffentlichung überfällig", "Publication overdue") : t("Entwurf überfällig", "Draft overdue"), "error");
  return pill(deliverableStatusLabel(x.status, currentLang()), STAND_TON[x.status] ?? "neutral");
}

export function beitragsName(x: Pick<BeitragRow, "format" | "format_label" | "platform" | "title">, katalog: readonly CatalogEntry[], opts: { title?: boolean } = {}): string {
  return deliverableLabel(x, (k) => platformLabel(k, katalog), "de", opts);
}

/** Zeile „Entwurf bis 01.10. · posten am 15.10.“ bzw. „live seit 20.09.“ */
export function fristText(x: Pick<BeitragRow, "status" | "draft_due" | "publish_on" | "published_on">): string {
  if (x.status === "live" && x.published_on) return t(`live seit ${date(x.published_on)}`, `live since ${date(x.published_on)}`);
  return [x.draft_due ? t(`Entwurf bis ${date(x.draft_due)}`, `Draft due ${date(x.draft_due)}`) : null, x.publish_on ? t(`posten am ${date(x.publish_on)}`, `post on ${date(x.publish_on)}`) : null].filter(Boolean).join(" · ") || t("ohne Termin", "no date");
}

export const FORMAT_OPTIONEN: readonly { key: string; label: string }[] = DELIVERABLE_FORMATS.map((k) => ({ key: k, get label() { return k === "other" ? t("Anderes Format …", "Other format …") : deliverableFormatLabel(k, null, currentLang()); } }));

/** Plattformen zur Auswahl: freigeschaltete des Katalogs, dazu die aktuell gewählte (Bestand bleibt gültig). */
export function plattformOptionen(katalog: readonly CatalogEntry[], aktuell?: string | null): { key: string; label: string }[] {
  const list = katalog.filter((c) => c.active || c.key === aktuell).map((c) => ({ key: c.key, label: platformLabel(c.key, katalog) }));
  if (list.length) return list;
  return ["instagram", "tiktok", "youtube", "other"].map((k) => ({ key: k, label: platformLabel(k) }));
}

/** Plattform, die zum Format passt, sofern die Agentur sie freigeschaltet hat; sonst die erste freigeschaltete. */
export function vorschlagPlattform(format: string, katalog: readonly CatalogEntry[], bisher?: string): string {
  const passend = isDeliverableFormat(format) ? DELIVERABLE_FORMAT_PLATFORM[format] : undefined;
  const aktiv = plattformOptionen(katalog);
  if (passend && aktiv.some((p) => p.key === passend)) return passend;
  if (bisher && aktiv.some((p) => p.key === bisher)) return bisher;
  return aktiv[0]?.key ?? "other";
}

// ── Formular ─────────────────────────────────────────────────────────────────

export interface BeitragFormular {
  anzahl: string;
  abstand: string;
  format: string;
  formatLabel: string;
  platform: string;
  titel: string;
  entwurf: string;
  posten: string;
  rechteMonate: string;
  /** 0199: organic, paid, whitelisting */
  rechteArten: string[];
  rechteExklusiv: boolean;
  /** "" = ohne Angabe */
  rechteGebiet: string;
  notiz: string;
  /** "" = ohne Monat */
  periodId: string;
  /** Entwurf vorab freigeben (PRAXIS-R3-5); Vorbelegung nach dem Format aus den Einstellungen der Agentur */
  freigabe: boolean;
}

/** Vorbelegung „Entwurf vorab freigeben“ je Format: nein für die Formate, die die Agentur ohne Vorabfreigabe führt */
export function freigabeVorbelegung(format: string, ohneFreigabe: readonly string[] | null | undefined): boolean {
  return !(ohneFreigabe ?? []).includes(format);
}

export function leeresBeitragsFormular(opts: { periodId?: string | null; katalog: readonly CatalogEntry[]; format?: string; entwurf?: string | null; ohneFreigabe?: readonly string[] | null }): BeitragFormular {
  const format = opts.format ?? "reel";
  const freigabe = freigabeVorbelegung(format, opts.ohneFreigabe);
  return {
    anzahl: "1", abstand: "0", format, formatLabel: "", platform: vorschlagPlattform(format, opts.katalog), titel: "", entwurf: freigabe ? opts.entwurf ?? "" : "", posten: "",
    rechteMonate: "", rechteArten: [], rechteExklusiv: false, rechteGebiet: "", notiz: "", periodId: opts.periodId ?? "", freigabe,
  };
}

export function formularAus(x: BeitragRow): BeitragFormular {
  return {
    anzahl: "1", abstand: "0", format: x.format, formatLabel: x.format_label ?? "", platform: x.platform, titel: x.title ?? "", entwurf: x.draft_due ?? "",
    posten: x.publish_on ?? "", rechteMonate: x.usage_rights_months != null ? String(x.usage_rights_months) : "",
    rechteArten: [...(x.usage_kinds ?? [])], rechteExklusiv: !!x.usage_exclusive, rechteGebiet: x.usage_territory ?? "", notiz: x.note ?? "", periodId: x.period_id ?? "",
    freigabe: x.approval_required !== false,
  };
}

export interface BeitragPruefung {
  fehler: Partial<Record<keyof BeitragFormular, string>>;
  /** Neue Zeilen (ohne org_id und deal_id) bzw. die Änderung eines Beitrags; null bei Fehlern */
  zeilen: Record<string, unknown>[] | null;
}

/**
 * CREATOR-R2-7: Liegt die Entwurfsfrist nach der Content-Deadline des Monats (bzw. der Kooperation), widersprechen sich die
 * Fristen. Der Creator sieht bei Beiträgen nur die Fristen je Beitrag (Übersicht, Kalender, Erinnerung); das Team sieht
 * beide. Nur ein Hinweis, kein Fehler: die Agentur entscheidet, welche Frist gilt.
 */
export function monatsfristHinweis(entwurf: string, monatsfrist: string | null | undefined): string | null {
  if (!entwurf || !monatsfrist || entwurf <= monatsfrist) return null;
  return t(`Die Entwurfsfrist liegt nach der Content-Deadline (${date(monatsfrist)}). Der Creator sieht bei Beiträgen nur die Frist je Beitrag; passe die Content-Deadline an, wenn sie nicht mehr gilt.`,
    `The draft deadline is after the content deadline (${date(monatsfrist)}). With posts the creator only sees the deadline per post; adjust the content deadline if it no longer applies.`);
}

const ganz = (v: string) => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN);

/** Prüft wie die Datenbank (Format benennen, Datumsfolge, Monate 1 bis 120) und liefert die Zeilen. */
export function pruefeBeitrag(f: BeitragFormular, opts: { neu: boolean; sortStart?: number }): BeitragPruefung {
  const fehler: BeitragPruefung["fehler"] = {};
  const anzahl = opts.neu ? ganz(f.anzahl) : 1;
  if (opts.neu && (!Number.isInteger(anzahl) || anzahl < 1 || anzahl > 20)) fehler.anzahl = t("Bitte 1 bis 20 Beiträge.", "Please 1 to 20 posts.");
  const abstand = f.abstand.trim() ? ganz(f.abstand) : 0;
  if (opts.neu && (!Number.isInteger(abstand) || abstand < 0 || abstand > 60)) fehler.abstand = t("Abstand 0 bis 60 Tage.", "Interval 0 to 60 days.");
  if (!isDeliverableFormat(f.format)) fehler.format = t("Bitte ein Format wählen.", "Please choose a format.");
  if (f.format === "other" && !f.formatLabel.trim()) fehler.formatLabel = t("Bitte das Format benennen.", "Please name the format.");
  if (f.formatLabel.trim().length > 60) fehler.formatLabel = t("Höchstens 60 Zeichen.", "At most 60 characters.");
  if (!f.platform) fehler.platform = t("Bitte eine Plattform wählen.", "Please choose a platform.");
  if (f.titel.trim().length > 120) fehler.titel = t("Höchstens 120 Zeichen.", "At most 120 characters.");
  if (f.entwurf && f.posten && f.entwurf > f.posten) fehler.entwurf = t("Die Entwurfsfrist liegt nach dem Termin der Veröffentlichung.", "The draft deadline is after the publication date.");
  const monate = f.rechteMonate.trim() ? ganz(f.rechteMonate) : null;
  if (monate !== null && (!Number.isInteger(monate) || monate < 1 || monate > 120)) fehler.rechteMonate = t("1 bis 120 Monate oder leer lassen.", "1 to 120 months or leave empty.");
  if (f.notiz.length > 2000) fehler.notiz = t("Höchstens 2000 Zeichen.", "At most 2000 characters.");
  if (Object.keys(fehler).length) return { fehler, zeilen: null };
  const basis = {
    period_id: f.periodId || null, platform: f.platform, format: f.format, format_label: f.formatLabel.trim() || null, title: f.titel.trim() || null,
    usage_rights_months: monate, usage_kinds: f.rechteArten, usage_exclusive: f.rechteExklusiv, usage_territory: f.rechteGebiet || null,
    note: f.notiz.trim() || null, approval_required: f.freigabe,
  };
  if (!opts.neu) return { fehler, zeilen: [{ ...basis, draft_due: f.entwurf || null, publish_on: f.posten || null }] };
  const serie = plannedSeries(anzahl, { draft_due: f.entwurf || null, publish_on: f.posten || null }, abstand);
  const start = opts.sortStart ?? 100;
  return { fehler, zeilen: serie.map((s, i) => ({ ...basis, ...s, sort_order: start + i })) };
}

// ── Anlegen mit der Kooperation (kleine Liste statt nur Freitext) ───────────────────────────────────────────────

export interface ListenZeile {
  anzahl: string;
  format: string;
  formatLabel: string;
  platform: string;
}

/** Zeilen der kleinen Liste im Formular „Kooperation anlegen“ → Beiträge (ohne Monat und Termine; die folgen in der Kooperation). */
export function zeilenAusListe(liste: readonly ListenZeile[]): { zeilen: Record<string, unknown>[]; fehler: string | null } {
  const out: Record<string, unknown>[] = [];
  for (const z of liste) {
    const n = ganz(z.anzahl);
    if (!Number.isInteger(n) || n < 1 || n > 20) return { zeilen: [], fehler: t("Je Zeile 1 bis 20 Beiträge.", "1 to 20 posts per row.") };
    if (!isDeliverableFormat(z.format)) return { zeilen: [], fehler: t("Bitte je Zeile ein Format wählen.", "Please choose a format per row.") };
    if (z.format === "other" && !z.formatLabel.trim()) return { zeilen: [], fehler: t("Bitte das andere Format benennen.", "Please name the other format.") };
    for (let i = 0; i < n; i++) {
      out.push({ platform: z.platform, format: z.format, format_label: z.formatLabel.trim() || null, sort_order: 100 + out.length });
    }
  }
  return { zeilen: out, fehler: null };
}

/** Freitext der Leistungen aus der Liste („2 × Reel · Instagram, Story · Instagram“), wenn der Freitext leer ist. */
export function freitextAusListe(liste: readonly ListenZeile[], katalog: readonly CatalogEntry[]): string | null {
  const r = zeilenAusListe(liste);
  if (r.fehler) return null;
  return summarizeDeliverables(r.zeilen.map((z) => ({ status: "planned", format: String(z.format), format_label: (z.format_label as string | null) ?? null, platform: String(z.platform) })),
    (k) => platformLabel(k, katalog));
}

// ── Übersicht des Teams ─────────────────────────────────────────────────────────

export type BeitragsFilter = "offen" | "ueberfaellig" | "woche" | "pruefung" | "marke" | "kennzeichnung" | "alle";
export const FILTER_LABEL: Record<BeitragsFilter, string> = {
  get offen() { return t("Offen", "Open"); }, get ueberfaellig() { return t("Überfällig", "Overdue"); }, get woche() { return t("Fällig diese Woche", "Due this week"); }, get pruefung() { return t("Zur Prüfung", "For review"); }, get marke() { return t("Bei der Marke", "With the brand"); }, get kennzeichnung() { return t("Live ohne Kennzeichnungsprüfung", "Live without disclosure check"); }, get alle() { return t("Alle", "All"); },
};
/** 0073: „Zur Prüfung“ (eingereicht, beim Team) und „Bei der Marke“ (offener Freigabe-Link) getrennt */
export const FILTER_BUCKET: Partial<Record<BeitragsFilter, DeliverableBucket>> = {
  ueberfaellig: "overdue", woche: "due_week", pruefung: "in_review", marke: "with_brand", kennzeichnung: "live_unchecked",
};
const LEER: () => Record<DeliverableBucket, number> = () => ({ overdue: 0, due_week: 0, in_review: 0, with_brand: 0, live_unchecked: 0 });

export interface UebersichtZeile {
  row: BeitragRow;
  deal: DealFull;
  talent: string;
  brand: string;
  name: string;
  /** Fristen gelten nur in laufenden Kooperationen und Monaten */
  aktiv: boolean;
  buckets: DeliverableBucket[];
  /** Datum zum Sortieren und Anzeigen: nächste Frist, sonst Veröffentlichung */
  tag: string | null;
  month: string | null;
  /** 0073: offener Freigabe-Link bei der Marke (seit wann, an wen) */
  marke: MarkenLink | null;
}

/** Offener Freigabe-Link eines Beitrags (brand_reviews, Stand „open“ und nicht abgelaufen). */
export interface MarkenLink {
  deliverable_id: string | null;
  created_at: string;
  expires_at: string;
  recipient_email: string;
  first_opened_at: string | null;
}

/** Laufende Kooperation und Monat (wie app.deliverable_active): nur dort gibt es Fristen und Überfälliges. */
export function beitragAktiv(x: Pick<BeitragRow, "status" | "period_id">, deal: Pick<DealFull, "archived_at" | "status"> | undefined, period: Pick<PeriodFull, "status" | "manual_status"> | undefined): boolean {
  if (!deal || x.status === "cancelled" || deal.archived_at || ["done", "blocked", "cancelled"].includes(deal.status)) return false;
  if (x.period_id && period && (period.status === "cancelled" || period.manual_status === "cancelled")) return false;
  return true;
}

export function uebersichtZeilen(d: {
  beitraege: readonly BeitragRow[]; deals: readonly DealFull[]; periods: readonly PeriodFull[]; talents: readonly TalentRow[]; brands: readonly BrandRow[];
  platforms: readonly CatalogEntry[]; today: string; beiMarke?: readonly MarkenLink[];
}): UebersichtZeile[] {
  const marke = new Map((d.beiMarke ?? []).filter((r) => r.deliverable_id && r.expires_at > new Date().toISOString()).map((r) => [r.deliverable_id!, r]));
  const deals = new Map(d.deals.map((x) => [x.id, x]));
  const periods = new Map(d.periods.map((p) => [p.id, p]));
  const talents = new Map(d.talents.map((t) => [t.id, t]));
  const brands = new Map(d.brands.map((b) => [b.id, b]));
  const out: UebersichtZeile[] = [];
  for (const row of d.beitraege) {
    const deal = deals.get(row.deal_id);
    if (!deal) continue;
    const period = row.period_id ? periods.get(row.period_id) : undefined;
    const aktiv = beitragAktiv(row, deal, period);
    const link = marke.get(row.id) ?? null;
    const buckets = aktiv ? deliverableBuckets({ ...row, at_brand: !!link }, d.today) : row.status === "live" && !row.label_checked ? ["live_unchecked" as const] : [];
    out.push({
      row, deal, aktiv, buckets, marke: link,
      talent: nameOfTalent(talents.get(deal.talent_id)), brand: brands.get(deal.brand_id)?.name ?? "–",
      name: beitragsName(row, d.platforms),
      tag: nextDue(row)?.date ?? row.published_on ?? row.publish_on ?? row.draft_due ?? null,
      month: period?.month.slice(0, 7) ?? null,
    });
  }
  return out;
}

export function filtereBeitraege(zeilen: readonly UebersichtZeile[], f: { filter: BeitragsFilter; talentId?: string; brandId?: string; suche?: string }): UebersichtZeile[] {
  const b = FILTER_BUCKET[f.filter];
  const q = (f.suche ?? "").trim().toLowerCase();
  return zeilen.filter((z) => {
    if (f.talentId && z.deal.talent_id !== f.talentId) return false;
    if (f.brandId && z.deal.brand_id !== f.brandId) return false;
    if (q && !`${z.name} ${z.talent} ${z.brand} ${z.deal.title}`.toLowerCase().includes(q)) return false;
    if (f.filter === "alle") return true;
    if (f.filter === "offen") return z.aktiv && z.row.status !== "live";
    return !!b && z.buckets.includes(b);
  }).sort(sortiereZeilen);
}

/** Überfälliges zuerst, dann nach Datum, ohne Datum am Ende. */
export function sortiereZeilen(a: UebersichtZeile, b: UebersichtZeile): number {
  const o = Number(b.buckets.includes("overdue")) - Number(a.buckets.includes("overdue"));
  if (o) return o;
  if (a.tag !== b.tag) return !a.tag ? 1 : !b.tag ? -1 : a.tag.localeCompare(b.tag);
  return a.name.localeCompare(b.name, "de");
}

export function zaehleBeitraege(zeilen: readonly UebersichtZeile[]): Record<DeliverableBucket, number> {
  const n = LEER();
  for (const z of zeilen) for (const b of z.buckets) n[b] += 1;
  return n;
}

/** Zahlen für die Übersicht /agentur: nur Beiträge laufender Kooperationen (Kennzeichnung auch danach). */
export function beitragsZahlen(d: { beitraege: readonly BeitragRow[]; deals: readonly Pick<DealFull, "id" | "archived_at" | "status">[];
  periods: readonly Pick<PeriodFull, "id" | "status" | "manual_status">[]; today: string; beiMarke?: readonly MarkenLink[] }): Record<DeliverableBucket, number> {
  const deals = new Map(d.deals.map((x) => [x.id, x]));
  const periods = new Map(d.periods.map((p) => [p.id, p]));
  const marke = new Set((d.beiMarke ?? []).filter((r) => r.expires_at > new Date().toISOString()).map((r) => r.deliverable_id));
  const n = LEER();
  for (const b of d.beitraege) {
    const aktiv = beitragAktiv(b, deals.get(b.deal_id), b.period_id ? periods.get(b.period_id) : undefined);
    const buckets = aktiv ? deliverableBuckets({ ...b, at_brand: marke.has(b.id) }, d.today) : b.status === "live" && !b.label_checked && deals.has(b.deal_id) ? ["live_unchecked" as const] : [];
    for (const k of buckets) n[k] += 1;
  }
  return n;
}

export function istFilter(v: string | null): v is BeitragsFilter {
  return !!v && v in FILTER_LABEL;
}
